import { useEffect, useState } from 'react';

/**
 * Reactive boolean: `true` while the viewport is at or below `maxWidth` px.
 *
 * Uses `window.matchMedia` + `change` events instead of a `resize` listener
 * — only fires when the breakpoint is actually crossed, so it's safe to
 * call in hot components without throttling.
 *
 * SSR-safe: returns `false` on the server (no `window`); the first client
 * render syncs to the real viewport before paint via a layout-effect-like
 * `useEffect` that runs synchronously on mount.
 */
export function useIsMobileViewport(maxWidth: number = 768): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [maxWidth]);

  return isMobile;
}

export default useIsMobileViewport;
